import { useState, useRef, useEffect } from 'react';
import { Camera, CheckCircle, XCircle, AlertCircle, Loader } from 'lucide-react';

interface TestResult {
  name: string;
  status: 'pending' | 'running' | 'success' | 'error';
  message: string;
  duration?: number;
}

export const AIProctorTest = () => {
  const [tests, setTests] = useState<TestResult[]>([
    { name: 'TensorFlow.js Core', status: 'pending', message: 'Not started' },
    { name: 'Webcam Access', status: 'pending', message: 'Not started' },
    { name: 'Face Detection Model', status: 'pending', message: 'Not started' },
    { name: 'Object Detection Model', status: 'pending', message: 'Not started' },
    { name: 'Live Face Detection', status: 'pending', message: 'Not started' },
    { name: 'Live Object Detection', status: 'pending', message: 'Not started' },
  ]);

  const [isRunning, setIsRunning] = useState(false);
  const [webcamEnabled, setWebcamEnabled] = useState(false);
  const [faceCount, setFaceCount] = useState(0);
  const [detectedObjects, setDetectedObjects] = useState<string[]>([]);
  const [logs, setLogs] = useState<string[]>([]);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const faceDetectorRef = useRef<any>(null);
  const objectDetectorRef = useRef<any>(null);
  const detectionIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, `[${timestamp}] ${message}`]);
    console.log(`[AIProctorTest] ${message}`);
  };

  const updateTest = (index: number, status: TestResult['status'], message: string, duration?: number) => {
    setTests(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], status, message, duration };
      return updated;
    });
  };

  const runAllTests = async () => {
    setIsRunning(true);
    setLogs([]);
    addLog('🚀 Starting AI Proctoring Tests...');

    // Test 1: TensorFlow.js Core
    await testTensorFlowCore();

    // Test 2: Webcam Access
    await testWebcamAccess();

    // Test 3: Face Detection Model
    await testFaceDetectionModel();

    // Test 4: Object Detection Model
    await testObjectDetectionModel();

    // Test 5 & 6: Live Detection
    await startLiveDetection();

    setIsRunning(false);
    addLog('✅ All tests completed!');
  };

  const testTensorFlowCore = async () => {
    updateTest(0, 'running', 'Loading TensorFlow.js...');
    const startTime = Date.now();

    try {
      addLog('Loading @tensorflow/tfjs...');
      const tf = await import('@tensorflow/tfjs');

      addLog('Initializing TensorFlow backend...');
      await tf.ready();

      const backend = tf.getBackend();
      const version = tf.version.tfjs;

      const duration = Date.now() - startTime;
      addLog(`✅ TensorFlow.js v${version} ready (backend: ${backend})`);
      updateTest(0, 'success', `Loaded v${version} with ${backend} backend`, duration);

      return true;
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMsg = error instanceof Error ? error.message : String(error);
      addLog(`❌ TensorFlow.js failed: ${errorMsg}`);
      updateTest(0, 'error', errorMsg, duration);
      return false;
    }
  };

  const testWebcamAccess = async () => {
    updateTest(1, 'running', 'Requesting webcam permission...');
    const startTime = Date.now();

    try {
      addLog('Requesting getUserMedia...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480 },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        streamRef.current = stream;
        setWebcamEnabled(true);
      }

      const duration = Date.now() - startTime;
      addLog(`✅ Webcam access granted (${stream.getVideoTracks()[0].label})`);
      updateTest(1, 'success', `Webcam enabled: ${stream.getVideoTracks()[0].label}`, duration);
      return true;
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMsg = error instanceof Error ? error.message : String(error);
      addLog(`❌ Webcam access failed: ${errorMsg}`);
      updateTest(1, 'error', errorMsg, duration);
      return false;
    }
  };

  const testFaceDetectionModel = async () => {
    updateTest(2, 'running', 'Loading face detection model...');
    const startTime = Date.now();

    try {
      addLog('Loading @tensorflow-models/face-landmarks-detection...');
      const faceLandmarksDetection = await import('@tensorflow-models/face-landmarks-detection');

      addLog('Creating MediaPipe FaceMesh detector...');
      const detector = await faceLandmarksDetection.createDetector(
        faceLandmarksDetection.SupportedModels.MediaPipeFaceMesh,
        {
          runtime: 'tfjs',
          refineLandmarks: true,
          maxFaces: 5,
        }
      );

      faceDetectorRef.current = detector;

      const duration = Date.now() - startTime;
      addLog(`✅ Face detection model loaded (MediaPipe FaceMesh)`);
      updateTest(2, 'success', 'MediaPipe FaceMesh loaded successfully', duration);
      return true;
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMsg = error instanceof Error ? error.message : String(error);
      addLog(`❌ Face detection model failed: ${errorMsg}`);
      updateTest(2, 'error', errorMsg, duration);
      return false;
    }
  };

  const testObjectDetectionModel = async () => {
    updateTest(3, 'running', 'Loading object detection model...');
    const startTime = Date.now();

    try {
      addLog('Loading @tensorflow-models/coco-ssd...');
      const cocoSsd = await import('@tensorflow-models/coco-ssd');

      addLog('Loading COCO-SSD model (lite_mobilenet_v2)...');
      const model = await cocoSsd.load({
        base: 'lite_mobilenet_v2',
      });

      objectDetectorRef.current = model;

      const duration = Date.now() - startTime;
      addLog(`✅ Object detection model loaded (COCO-SSD)`);
      updateTest(3, 'success', 'COCO-SSD lite_mobilenet_v2 loaded', duration);
      return true;
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMsg = error instanceof Error ? error.message : String(error);
      addLog(`❌ Object detection model failed: ${errorMsg}`);
      updateTest(3, 'error', errorMsg, duration);
      return false;
    }
  };

  const startLiveDetection = async () => {
    if (!videoRef.current || !faceDetectorRef.current || !objectDetectorRef.current) {
      addLog('⚠️ Cannot start live detection - missing requirements');
      return;
    }

    updateTest(4, 'running', 'Detecting faces...');
    updateTest(5, 'running', 'Detecting objects...');

    addLog('🔄 Starting live detection loop...');

    let faceDetections = 0;
    let objectDetections = 0;

    const runDetection = async () => {
      if (!videoRef.current || videoRef.current.readyState !== 4) {
        addLog(`⚠️ Video not ready (readyState: ${videoRef.current?.readyState})`);
        return;
      }

      try {
        // Clear canvas once at the start
        if (canvasRef.current && videoRef.current) {
          const canvas = canvasRef.current;
          const video = videoRef.current;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
          }
        }

        // Face Detection
        let faces: any[] = [];
        if (faceDetectorRef.current) {
          const detectionStartTime = Date.now();
          faces = await faceDetectorRef.current.estimateFaces(videoRef.current, {
            flipHorizontal: false,
          });
          const detectionTime = Date.now() - detectionStartTime;

          setFaceCount(faces.length);
          faceDetections++;

          if (faceDetections === 1) {
            updateTest(4, 'success', `Detected ${faces.length} face(s)`);
            addLog(`✅ Face detection working: ${faces.length} face(s) found (${detectionTime}ms)`);
          } else if (faceDetections % 5 === 0) {
            updateTest(4, 'success', `${faceDetections} detections - ${faces.length} face(s)`);
            addLog(`📊 Face detection: ${faces.length} face(s) | Detection time: ${detectionTime}ms`);
          }

          // Log face details for debugging
          if (faces.length > 0) {
            addLog(`🔍 Face details: ${faces.map((f, i) => `Face ${i+1} at (${Math.round(f.box.xMin)}, ${Math.round(f.box.yMin)}) size=${Math.round(f.box.width)}x${Math.round(f.box.height)}`).join(', ')}`);
          } else if (faceDetections % 3 === 0) {
            // Only log warning every 3rd detection to reduce spam
            addLog(`⚠️ No faces detected (detection #${faceDetections}) - Try: face camera directly, improve lighting, move closer`);
          }
        }

        // Object Detection
        let predictions: any[] = [];
        if (objectDetectorRef.current) {
          predictions = await objectDetectorRef.current.detect(videoRef.current);

          const objects = predictions.map((p: any) => p.class);
          setDetectedObjects(objects);
          objectDetections++;

          if (objectDetections === 1) {
            updateTest(5, 'success', `Detected ${predictions.length} object(s)`);
            addLog(`✅ Object detection working: ${predictions.length} object(s) found`);
          } else if (objectDetections % 5 === 0) {
            updateTest(5, 'success', `${objectDetections} detections - ${predictions.length} object(s)`);
            addLog(`📊 Objects: ${predictions.map(p => `${p.class} (${Math.round(p.score * 100)}%)`).join(', ')}`);
          }
        }

        // Draw both on the same canvas
        drawDetections(faces, predictions);
      } catch (error) {
        console.error('Detection error:', error);
        addLog(`❌ Detection error: ${error instanceof Error ? error.message : String(error)}`);
      }
    };

    // Run detection every 2 seconds
    detectionIntervalRef.current = setInterval(runDetection, 2000);

    // Run first detection immediately
    runDetection();
  };

  const drawDetections = (faces: any[], predictions: any[]) => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Draw face bounding boxes (GREEN)
    if (faces.length > 0) {
      ctx.strokeStyle = '#00ff00';
      ctx.lineWidth = 4;
      ctx.fillStyle = '#00ff00';
      ctx.font = 'bold 18px Arial';

      faces.forEach((face, index) => {
        const box = face.box;
        ctx.strokeRect(box.xMin, box.yMin, box.width, box.height);
        ctx.fillText(`Face ${index + 1}`, box.xMin, box.yMin - 10);
      });
    }

    // Draw object bounding boxes (RED)
    if (predictions.length > 0) {
      ctx.strokeStyle = '#ff0000';
      ctx.lineWidth = 3;
      ctx.fillStyle = '#ff0000';
      ctx.font = 'bold 16px Arial';

      predictions.forEach((prediction) => {
        const [x, y, width, height] = prediction.bbox;
        ctx.strokeRect(x, y, width, height);
        ctx.fillText(
          `${prediction.class} (${Math.round(prediction.score * 100)}%)`,
          x,
          y - 10
        );
      });
    }
  };

  const stopTests = () => {
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    setWebcamEnabled(false);
    setIsRunning(false);
    addLog('🛑 Tests stopped');
  };

  useEffect(() => {
    return () => {
      if (detectionIntervalRef.current) {
        clearInterval(detectionIntervalRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const getStatusIcon = (status: TestResult['status']) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'running':
        return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            AI Proctoring Test Suite
          </h1>
          <p className="text-gray-600 mb-6">
            Test TensorFlow.js AI proctoring components (Face Detection + Object Detection)
          </p>

          <div className="flex gap-4 mb-6">
            <button
              onClick={runAllTests}
              disabled={isRunning}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isRunning ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Running Tests...
                </>
              ) : (
                <>
                  <Camera className="w-5 h-5" />
                  Run All Tests
                </>
              )}
            </button>

            {isRunning && (
              <button
                onClick={stopTests}
                className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Stop Tests
              </button>
            )}
          </div>

          {/* Test Results */}
          <div className="space-y-3 mb-6">
            {tests.map((test, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(test.status)}
                  <div>
                    <h3 className="font-semibold text-gray-900">{test.name}</h3>
                    <p className="text-sm text-gray-600">{test.message}</p>
                  </div>
                </div>
                {test.duration && (
                  <span className="text-sm text-gray-500">{test.duration}ms</span>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mb-6">
          {/* Video Feed */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Live Camera Feed</h2>
            <div className="relative bg-black rounded-lg overflow-hidden">
              <video
                ref={videoRef}
                className="w-full h-auto"
                autoPlay
                playsInline
                muted
              />
              <canvas
                ref={canvasRef}
                className="absolute top-0 left-0 w-full h-full"
              />
              {!webcamEnabled && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <p className="text-white text-lg">Webcam not enabled</p>
                </div>
              )}
            </div>
          </div>

          {/* Detection Results */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Detection Results</h2>

            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-900 mb-2">Face Detection</h3>
                <p className="text-3xl font-bold text-green-600">{faceCount}</p>
                <p className="text-sm text-green-700">Face(s) detected</p>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-semibold text-blue-900 mb-2">Object Detection</h3>
                <p className="text-sm text-blue-700 mb-2">
                  {detectedObjects.length} object(s) detected:
                </p>
                <div className="flex flex-wrap gap-2">
                  {detectedObjects.length > 0 ? (
                    detectedObjects.map((obj, i) => (
                      <span
                        key={i}
                        className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs"
                      >
                        {obj}
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500 text-sm">No objects detected</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Console Logs */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Console Logs</h2>
          <div className="bg-gray-900 text-green-400 rounded-lg p-4 font-mono text-sm h-64 overflow-y-auto">
            {logs.length > 0 ? (
              logs.map((log, i) => (
                <div key={i} className="mb-1">
                  {log}
                </div>
              ))
            ) : (
              <div className="text-gray-500">No logs yet. Click "Run All Tests" to start.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
